import { Magnifier } from '@volkswagen-onehub/icons-core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FIELD_SUFFIX, FORM_MESSAGE, FORM_SUBMIT_STATUS } from '../../../constants/form-constants';
import { BORDER, COLOR_TOKEN } from '../../../constants/styles-constants';
import { formService } from '../../../form-service';
import { TextInputExtended } from './core/text-input-extended';

interface Props {
	name: string;
	label: string;
	suggestions: string[];
	value?: string;
	required?: boolean;
	inputRef?: React.RefObject<HTMLInputElement>;
	hideSuggestions?: boolean;
	icon?: React.ReactNode;
	maxLength?: number;
	expectingSuggestions?: boolean;
	onSuggestionClick: (suggestion: string) => void;
	onChange?: (e: React.SyntheticEvent<HTMLInputElement>) => void;
	onFocus?: (e: React.SyntheticEvent<HTMLInputElement>) => void;
}

export function SuggestionInput(props: Props) {
	const {
		name,
		value,
		required,
		inputRef,
		label,
		suggestions,
		maxLength,
		hideSuggestions,
		icon,
		expectingSuggestions,
		onSuggestionClick,
		onChange,
		onFocus,
	} = props;
	const suggestionsRef = useRef<HTMLDivElement>(null);
	const [suggestionSelected, setSuggestionSelected] = useState(false);
	const [showError, setShowError] = useState(false);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
	const suggestionFieldName = `${name}_${FIELD_SUFFIX.SUGGESTION_SELECTED}`;

	useEffect(() => {
		formService.setFieldValue(suggestionFieldName, false);
		const unsubscribe = formService.subscribeToFormSubmitStatus((status) => {
			if (status === FORM_SUBMIT_STATUS.INVALID) {
				const selected = formService.getFieldValue(suggestionFieldName);
				setShowError(!selected);
			}
		});
		return unsubscribe;
	}, [suggestionFieldName]);

	const errorMessage = useMemo(() => {
		if (expectingSuggestions && !suggestionSelected && suggestions.length === 0) {
			return 'No se han encontrado resultados';
		}
		if (showError && required && !suggestionSelected) {
			return 'Debes buscar y seleccionar una opción';
		}
		return undefined;
	}, [expectingSuggestions, suggestionSelected, suggestions, required, showError]);

	const infoMessage = useMemo(() => {
		if (expectingSuggestions && !suggestionSelected && suggestions.length > 0) {
			return FORM_MESSAGE.CHOOSE_OPTION;
		}
		return undefined;
	}, [expectingSuggestions, suggestionSelected, suggestions, required]);

	useEffect(() => {
		if (suggestions.length > 0 && !hideSuggestions && suggestionsRef.current) {
			suggestionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}, [suggestions.length, hideSuggestions]);

	return (
		<>
			<TextInputExtended
				name={name}
				required={required}
				value={value}
				onChange={(e) => {
					setSuggestionSelected(false);
					setShowError(false);
					formService.setFieldValue(suggestionFieldName, false);
					if (onChange) onChange(e);
				}}
				onFocus={onFocus}
				onKeyDown={(e) => {
					if (e.key === 'Enter' && suggestions.length > 0) {
						e.preventDefault();
						setSuggestionSelected(true);
						formService.setFieldValue(suggestionFieldName, true);
						onSuggestionClick(suggestions[0]);
					}
				}}
				label={label}
				icon={icon ?? <Magnifier variant="default" aria-hidden />}
				inputRef={inputRef}
				maxLength={maxLength}
				autoComplete="off"
				hasError={!!errorMessage}
				errorMessage={errorMessage}
				infoMessage={infoMessage}
			/>
			{suggestions.length > 0 && !hideSuggestions && (
				<div
					ref={suggestionsRef}
					style={{
						margin: '18px 15px 0px 15px',
						border: `${BORDER.size.default} solid ${COLOR_TOKEN.border.default}`,
						borderRadius: BORDER.radius.small,
					}}
				>
					{suggestions.map((suggestion, index) => (
						<div
							key={index}
							tabIndex={0}
							role="button"
							style={{
								padding: '8px 12px',
								cursor: 'pointer',
								borderTop: index > 0 ? `${BORDER.size.default} solid ${COLOR_TOKEN.border.default}` : 'none',
								color: hoveredIndex === index ? COLOR_TOKEN.content.hover : undefined,
							}}
							onMouseEnter={() => setHoveredIndex(index)}
							onMouseLeave={() => setHoveredIndex(null)}
							onClick={() => {
								setSuggestionSelected(true);
								formService.setFieldValue(suggestionFieldName, true);
								onSuggestionClick(suggestion);
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									setSuggestionSelected(true);
									formService.setFieldValue(suggestionFieldName, true);
									onSuggestionClick(suggestion);
								}
							}}
						>
							{suggestion}
						</div>
					))}
				</div>
			)}
		</>
	);
}
